import React, { useCallback, useState } from "react";

import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Box, Button, Link, Stack, Typography } from "@mui/material";
import { ProgressOverlay } from "components";
import { SimpleHeadMui } from "components/Simple-head/SimpleHeadMui";
import { useActionHook, useModals } from "hooks";
import { IApiServiceError, useDealActions } from "store";
import { IDealImportValidateResponse } from "store/types";
import pickFiles from "utils/pick-files";

const ACCEPTED_TYPES = ".csv,.xlsx";

type Step = "upload" | "pre-creation" | "errors";

interface Props {
  onCancel: () => void;
  onSubmit: (data: { id: number; title: string }) => void;
}

export const DealCreateFromFile: React.FC<Props> = ({ onCancel, onSubmit }) => {
  const { errorModal } = useModals();
  const { downloadDealImportTemplateAction, validateDealImportFileAction, createDealFromFileAction } = useDealActions();

  const [step, setStep] = useState<Step>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [validateResult, setValidateResult] = useState<IDealImportValidateResponse | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const { action: downloadTemplate, loading: downloadLoading } = useActionHook(downloadDealImportTemplateAction, {
    onReject: (error) => errorModal({ msg: (error as IApiServiceError).message || "Something went wrong" })
  });

  const { action: validateFile, loading: validateLoading } = useActionHook(validateDealImportFileAction, {
    onReject: (error) => errorModal({ msg: (error as IApiServiceError).message || "Something went wrong" })
  });
  const { action: createDeal, loading: createLoading } = useActionHook(createDealFromFileAction, {
    onReject: (error) => errorModal({ msg: (error as IApiServiceError).message || "Something went wrong" })
  });

  const handleDownloadTemplate = useCallback(async () => {
    await downloadTemplate();
  }, [downloadTemplate]);

  const handleBrowse = useCallback(async () => {
    const fileList = await pickFiles({ accept: ACCEPTED_TYPES });
    if (!fileList?.length) return;
    setFile(fileList[0]);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files?.[0];
    if (!dropped) return;
    const ext = dropped.name.split(".").pop()?.toLowerCase();
    if (ext === "csv" || ext === "xlsx") {
      setFile(dropped);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleUpload = useCallback(async () => {
    if (!file) return;
    const result = await validateFile(file);
    setValidateResult(result);
    if (result.valid) {
      setStep("pre-creation");
    } else {
      setStep("errors");
    }
  }, [file, validateFile]);

  const handleBackToUpload = useCallback(() => {
    setStep("upload");
    setFile(null);
    setValidateResult(null);
  }, []);

  const handleCreate = useCallback(async () => {
    if (!validateResult?.valid || !validateResult.dealName) return;
    const assetClassName = validateResult.asset_class_name ?? "";
    if (!assetClassName) return;
    const raw = Array.isArray(validateResult.characteristics) ? validateResult.characteristics : [];
    // Send explicit { name, value } so API receives a plain array (avoid serialization issues)
    const characteristics = raw.map((c) => ({
      name: String(c?.name ?? ""),
      value: String(c?.value ?? "")
    }));
    const { dealId, dealTitle } = await createDeal({
      dealName: validateResult.dealName,
      asset_class_name: assetClassName,
      characteristics
    });
    onSubmit({ id: dealId, title: dealTitle });
  }, [validateResult, createDeal, onSubmit]);

  const loading = downloadLoading || validateLoading || createLoading;

  return (
    <ProgressOverlay
      inProgress={loading}
      containerSX={{
        display: "flex",
        flexDirection: "column",
        maxHeight: "80vh",
        minWidth: "560px",
        maxWidth: "560px",
        flexGrow: 1,
        position: "absolute" as const,
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        padding: 0,
        border: "none",
        overflow: "hidden",
        borderRadius: "10px"
      }}
    >
      <SimpleHeadMui onIconClick={onCancel}>
        <Typography sx={{ color: "#0a3142", fontSize: "16px" }}>Starting From File</Typography>
      </SimpleHeadMui>
      <Stack
        sx={{
          padding: "24px 30px 24px 26px",
          flexGrow: 1,
          overflowY: "auto",
          backgroundColor: "#ffffff"
        }}
      >
        {step === "upload" && (
          <>
            <Stack gap="16px" sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: "14px", color: "#071F35" }}>
                Upload a file in the required format with the completed data.{" "}
                <Link
                  component="button"
                  variant="body2"
                  sx={{ cursor: "pointer", textDecoration: "underline" }}
                  onClick={handleDownloadTemplate}
                >
                  Download the <strong>Template File</strong> for example.
                </Link>
              </Typography>
              <Box
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                sx={{
                  border: "2px dashed",
                  borderColor: isDragging ? "primary.main" : "#DAE0E2",
                  borderRadius: 1,
                  p: 3,
                  textAlign: "center",
                  bgcolor: isDragging ? "action.hover" : "transparent",
                  cursor: "pointer"
                }}
                onClick={handleBrowse}
              >
                <UploadFileIcon sx={{ fontSize: 48, color: "#496682", mb: 1 }} />
                <Typography sx={{ fontSize: "14px", color: "#496682" }}>
                  {file ? file.name : "Drag and drop your file here, or click to browse"}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: "#8a9ba8", mt: 0.5 }}>
                  Accepted: .csv, .xlsx (single file)
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" justifyContent="flex-end" gap={1}>
              <Button variant="outlined" onClick={onCancel}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleUpload} disabled={!file}>
                Upload
              </Button>
            </Stack>
          </>
        )}

        {step === "pre-creation" && validateResult?.valid && file && (
          <>
            <Typography sx={{ fontSize: "14px", color: "#071F35", mb: 1 }}>
              Uploaded file: <strong>{file.name}</strong>
            </Typography>
            <Stack gap="8px" sx={{ mb: 2 }}>
              <Typography component="div" sx={{ fontSize: "14px", color: "#071F35" }}>
                <strong>Deal Name:</strong> {validateResult.dealName}
              </Typography>
              {validateResult.asset_class_name && (
                <Typography component="div" sx={{ fontSize: "14px", color: "#071F35" }}>
                  <strong>Asset Class:</strong> {validateResult.asset_class_name}
                </Typography>
              )}
              {validateResult.characteristics?.map((c) => (
                <Typography key={c.name} component="div" sx={{ fontSize: "14px", color: "#071F35" }}>
                  <strong>{c.name}:</strong> {c.value || "—"}
                </Typography>
              ))}
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Button variant="outlined" onClick={handleBackToUpload}>
                &lt; Back
              </Button>
              <Stack direction="row" gap={1}>
                <Button variant="outlined" onClick={onCancel}>
                  Cancel
                </Button>
                <Button variant="contained" onClick={handleCreate}>
                  Create
                </Button>
              </Stack>
            </Stack>
          </>
        )}

        {step === "errors" && validateResult && (
          <>
            <Stack gap="12px" sx={{ mb: 2 }}>
              {validateResult.errors?.map((err, i) => (
                <Typography key={i} sx={{ fontSize: "14px", color: "#071F35" }}>
                  <strong>{err.parameter}</strong>
                  {err.value !== undefined && <> — Value in file: {err.value === "" ? "(empty)" : err.value}</>}
                  {" — "}
                  {err.reason} {err.expectedFormat}
                </Typography>
              ))}
              {validateResult.newCharacteristics && validateResult.newCharacteristics.length > 0 && (
                <Typography sx={{ fontSize: "14px", color: "#071F35" }}>
                  This import can only populate existing fields. Remove these rows or create the characteristics in
                  Settings first: {validateResult.newCharacteristics.join(", ")}
                </Typography>
              )}
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Button variant="outlined" onClick={handleBackToUpload}>
                Back
              </Button>
              <Button variant="outlined" onClick={onCancel}>
                Cancel
              </Button>
            </Stack>
          </>
        )}
      </Stack>
    </ProgressOverlay>
  );
};
